import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../i18n";
import { api, formatApiError } from "../lib/api";
import { Trophy, Loader2, Mail, KeyRound } from "lucide-react";

const TAB_PASSWORD = "password";
const TAB_MAGIC = "magic";

export default function Login() {
    const { login } = useAuth();
    const { t } = useI18n();
    const nav = useNavigate();
    const loc = useLocation();
    const next = new URLSearchParams(loc.search).get("next") || "/dashboard";

    const [activeTab, setActiveTab] = useState(TAB_PASSWORD);
    // Show banner when the server redirected here with ?magic=invalid (the
    // user clicked a stale/expired magic link).
    const magicInvalid = new URLSearchParams(loc.search).get("magic") === "invalid";
    // password form
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    // magic-link form (separate state — different submission semantics)
    const [magicEmail, setMagicEmail] = useState("");
    const [magicErr, setMagicErr] = useState("");
    const [magicSent, setMagicSent] = useState(false);
    const [magicLoading, setMagicLoading] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErr("");
        try {
            await login(email, password);
            nav(next);
        } catch (e2) {
            setErr(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setLoading(false);
        }
    };

    const onMagicSubmit = async (e) => {
        e.preventDefault();
        setMagicLoading(true);
        setMagicErr("");
        try {
            await api.post("/auth/magic/request", { email: magicEmail });
            setMagicSent(true);
        } catch (e2) {
            setMagicErr(formatApiError(e2.response?.data?.detail) || t("auth.magic.error_send"));
        } finally {
            setMagicLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto px-4 pt-12 pb-20" data-testid="login-page">
            <div className="flex flex-col items-center mb-8">
                <div className="h-12 w-12 rounded-md bg-[var(--accent-hex)] flex items-center justify-center mb-3">
                    <Trophy className="h-6 w-6 text-[var(--accent-text)]" />
                </div>
                <h1 className="font-heading text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]">{t("auth.login.title")}</h1>
                <p className="text-sm text-[var(--text-secondary)] mt-1">{t("auth.login.subtitle")}</p>
            </div>

            {magicInvalid && (
                <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2.5 text-sm text-red-300" data-testid="magic-invalid-banner">
                    {t("auth.magic.error_invalid")}
                </div>
            )}

            {/* Method tabs */}
            <div className="grid grid-cols-2 gap-1 p-1 mb-4 rounded-md border border-[var(--border-base)] bg-[var(--bg-card)]" data-testid="login-tabs">
                <button type="button" onClick={() => setActiveTab(TAB_PASSWORD)} data-testid="tab-password"
                    className={`flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold rounded transition-colors ${
                        activeTab === TAB_PASSWORD ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}>
                    <KeyRound className="h-3.5 w-3.5" />
                    {t("auth.magic.tab_password")}
                </button>
                <button type="button" onClick={() => setActiveTab(TAB_MAGIC)} data-testid="tab-magic"
                    className={`flex items-center justify-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-bold rounded transition-colors ${
                        activeTab === TAB_MAGIC ? "bg-[var(--accent-hex)] text-[var(--accent-text)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                    }`}>
                    <Mail className="h-3.5 w-3.5" />
                    {t("auth.magic.tab")}
                </button>
            </div>

            {activeTab === TAB_PASSWORD && (
                <form onSubmit={onSubmit} className="space-y-4 p-6 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]" data-testid="password-form">
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.email")}</label>
                        <input
                            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)] focus:ring-1 focus:ring-[var(--accent-hex)]"
                        />
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.password")}</label>
                        <input
                            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)] focus:ring-1 focus:ring-[var(--accent-hex)]"
                        />
                    </div>
                    {err && <p className="text-sm text-[#FF3B30]" data-testid="login-error">{err}</p>}
                    <button
                        type="submit" disabled={loading} data-testid="login-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-3 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50 transition-colors"
                    >
                        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {t("auth.login.title")}
                    </button>
                    <p className="text-center text-xs text-[var(--text-secondary)]">
                        {t("auth.no_account")}{" "}
                        <Link to="/register" className="text-[var(--accent-hex)] hover:underline font-bold" data-testid="link-register">{t("auth.create_one")}</Link>
                    </p>
                </form>
            )}

            {activeTab === TAB_MAGIC && (
                <form onSubmit={onMagicSubmit} className="space-y-4 p-6 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]" data-testid="magic-form">
                    <div>
                        <h2 className="font-heading text-lg font-bold uppercase tracking-tight text-[var(--text-primary)]">{t("auth.magic.title")}</h2>
                        <p className="text-xs text-[var(--text-secondary)] mt-1">{t("auth.magic.subtitle")}</p>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-2">{t("auth.email")}</label>
                        <input
                            type="email" required value={magicEmail} onChange={(e) => setMagicEmail(e.target.value)}
                            placeholder={t("auth.magic.email_ph")}
                            data-testid="magic-email"
                            className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)] focus:ring-1 focus:ring-[var(--accent-hex)]"
                        />
                    </div>
                    {magicErr && <p className="text-sm text-[#FF3B30]" data-testid="magic-error">{magicErr}</p>}
                    {magicSent && <p className="text-sm text-[var(--accent-hex)]" data-testid="magic-sent">{t("auth.magic.sent")}</p>}
                    <button
                        type="submit" disabled={magicLoading || magicSent} data-testid="magic-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-3 rounded-md hover:bg-[var(--accent-hex-hover)] disabled:opacity-50 transition-colors"
                    >
                        {magicLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                        {magicSent ? t("auth.magic.sent") : t("auth.magic.send")}
                    </button>
                </form>
            )}
        </div>
    );
}
